import { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';

import { t, type UiLangCode } from '../lib/i18n';

const COUNTDOWN_SECONDS = 5;
const STRIPE_DONATION_URL = import.meta.env.VITE_STRIPE_DONATION_URL;

interface DonationDialogProps {
  lang: UiLangCode;
  /** Fires when the countdown reaches 0 OR the user clicks "Skip". */
  onProceed: () => void;
  /** Fires when the user dismisses the modal entirely (Escape / backdrop). */
  onClose: () => void;
}

/**
 * Pre-export modal: gates every CSV / XLSX / ZIP download behind a
 * 5-second countdown with a Stripe Payment Link "Donate" button. The
 * download fires automatically when the countdown ends; the user can
 * skip immediately or click the donate button (opens Stripe in a new
 * tab without affecting the timer).
 *
 * `VITE_STRIPE_DONATION_URL` gates the donate button — when unset the
 * modal still gates the download (so the timer behaves uniformly
 * across deploys) but renders no donate CTA.
 */
export function DonationDialog({ lang, onProceed, onClose }: DonationDialogProps): JSX.Element {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  // The parent passes inline arrows for onProceed, so its identity
  // changes on every parent re-render — several per second while other
  // receipts are still processing. Holding the latest callback in a ref
  // lets the countdown / Escape effects depend only on what actually
  // matters, instead of tearing down the 1s timeout each render (which
  // froze the countdown mid-batch).
  const onProceedRef = useRef(onProceed);
  useEffect(() => {
    onProceedRef.current = onProceed;
  }, [onProceed]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      onProceedRef.current();
      return;
    }
    const id = window.setTimeout(() => {
      setSecondsLeft((s) => s - 1);
    }, 1000);
    return () => window.clearTimeout(id);
  }, [secondsLeft]);

  // Escape key dismisses (treated like skip — fires the download).
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onProceedRef.current();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const countdownLabel =
    secondsLeft > 0
      ? t(lang, 'donate.countdown').replace('{seconds}', String(secondsLeft))
      : t(lang, 'donate.starting');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="donate-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 dark:bg-slate-950/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400">
            <Heart className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2
            id="donate-title"
            className="text-lg font-semibold text-slate-900 dark:text-slate-100"
          >
            {t(lang, 'donate.title')}
          </h2>
        </div>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          {t(lang, 'donate.body')}
        </p>
        <p className="mt-4 text-xs font-medium tabular-nums text-slate-500 dark:text-slate-400">
          {countdownLabel}
        </p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onProceed}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            {t(lang, 'donate.skip')}
          </button>
          {STRIPE_DONATION_URL !== undefined && STRIPE_DONATION_URL.trim() !== '' && (
            <a
              href={STRIPE_DONATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-rose-700"
            >
              <Heart className="h-4 w-4" aria-hidden="true" />
              {t(lang, 'donate.cta')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
